// core
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {PopoverController} from '@ionic/angular';
// models
import {OrderStatus, QueueInfo, StatusesFromServer} from '../../../core/models/order.model';
import {RestaurantAvailableStatus} from '../../../core/models/restaurant.model';
// stores
import {RestaurantStore} from '../../../core/stores/restaurant.store';
import {OrderStore} from '../../../core/stores/order.store';
// services
import {SoundNotificationsService} from '../../../core/services/sound-notifications.service';
import {PaymentService} from '../../../core/services/payment.service';
// static methods
// environment
import {environment} from '../../../../environments/environment';
// ionic plugins
// rxjs
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-orders-status',
  templateUrl: './orders-status.component.html',
  styleUrls: ['./orders-status.component.scss']
})
export class OrdersStatusComponent implements OnInit, OnDestroy {
  @Input() public locationId: string;
  @Input() public isGroup: boolean;
  @Input() public queueInfo: QueueInfo;

  public readonly isKiosk: boolean = environment.isKiosk;
  public readonly isKitchen: boolean = environment.isKitchen;

  public isEmptyOrders: boolean;
  public ordersQuantity: number;
  public ordersStatus: OrderStatus[];
  public isLoadingStatuses: boolean;
  public isLoadingStatusesUpdate: boolean;

  private readonly TIME_TO_REFRESH = 30000;
  private readonly isOnlyPending = true;
  private readonly newOrderStatus = StatusesFromServer[0];
  private readonly pathToSoundNotification = 'assets/sound-notifications/SimpleHigh.mp3';

  private refreshIntervalHandler: number;
  private soundIntervalHandler: number;
  private isHaveNewOrders: boolean;
  private destroyed$ = new Subject<void>();

  constructor(
    public orderStore: OrderStore,
    public restaurantStore: RestaurantStore,
    private paymentService: PaymentService,
    private router: Router,
    private route: ActivatedRoute,
    private popoverController: PopoverController,
    private soundNotification: SoundNotificationsService
  ) {
  }

  public static getQueueInfo(restaurantAvailableStatus: RestaurantAvailableStatus): QueueInfo {
    return {
      placeInLine: restaurantAvailableStatus.placeInLine,
      estimatedTimeToBeginPreparation: restaurantAvailableStatus.estimatedTimeToBeginPreparation,
      numPersonsAhead: restaurantAvailableStatus.numPersonsAhead,
      maxOrdersInQueue: restaurantAvailableStatus.maxOrdersInQueue
    };
  }

  public ngOnInit(): void {
    if (!this.isGroup) {
      this.getRestaurantAvailableStatus();
    }
    this.getIsLoading();
    this.setIntervalForStatus(this.locationId, this.isOnlyPending, this.isGroup);
    this.orderStore.bootstrapAllPendingOrderStatus(this.locationId, this.isOnlyPending, this.isGroup);
    this.orderStore.pendingOrdersStatus$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((orders: OrderStatus[]) => {
          if (orders) {
            this.ordersStatus = orders;
            this.ordersQuantity = orders.length;
            this.isEmptyOrders = orders.length === 0;

            if (this.isKitchen) {
              this.isHaveNewOrders = this.checkIsHaveNewOrders(orders);
              this.initSoundNotifications(this.pathToSoundNotification)
                .catch(error => console.error(error));
            }
          }
        }
      );
  }

  public ngOnDestroy(): void {
    if (this.refreshIntervalHandler) {
      clearInterval(this.refreshIntervalHandler);
    }
    if (this.soundIntervalHandler) {
      clearInterval(this.soundIntervalHandler);
    }
    this.destroyed$.next();
  }

  public close() {
    this.popoverController.dismiss();
  }

  private setIntervalForStatus(locationId: string, isOnlyPending: boolean, isGroup: boolean) {
    this.refreshIntervalHandler = window.setInterval((function (self: any) {
      return function () {
        self.orderStore.bootstrapAllPendingOrderStatus(locationId, isOnlyPending, isGroup);
      };
    })(this), this.TIME_TO_REFRESH);
  }

  private getRestaurantAvailableStatus() {
    this.restaurantStore
      .restaurantAvailableStatus$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(restaurantAvailableStatus => this.queueInfo = OrdersStatusComponent.getQueueInfo(restaurantAvailableStatus));
  }

  private getIsLoading(): void {
    this.orderStore.isLoadingStatuses$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(isLoading => this.isLoadingStatuses = isLoading);
    this.orderStore.isLoadingStatusesUpdate$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(isLoading => this.isLoadingStatusesUpdate = isLoading);
  }

  private checkIsHaveNewOrders(orders: OrderStatus[]): boolean {
    if (orders.length > 0) {
      let result: boolean;
      orders.forEach(order => {
        if (order.status === this.newOrderStatus) {
          result = true;
        }
      });
      return result ? result : null;
    }
  }

  private async initSoundNotifications(pathToSoundFile: string) {
    const id = 'newOrderNotification';
    await this.soundNotification.preloadAudio(id, pathToSoundFile);

    if (this.isHaveNewOrders) {
      await this.soundNotification.playAudio(id);

      if (!this.soundIntervalHandler) {
        this.soundIntervalHandler = window.setInterval(() => {
          this.soundNotification.playAudio(id);
        }, this.TIME_TO_REFRESH);
      }

    } else {
      clearInterval(this.soundIntervalHandler);
      this.soundIntervalHandler = null;
    }
  }
}
